import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Voicemail, Phone, CheckCircle, Circle, Calendar, Clock, Trash2 } from 'lucide-react'
import { fetchAuthSession } from 'aws-amplify/auth'
import { LoadingSpinner } from './LoadingSpinner'

interface VoicemailMessage {
  id: string // DynamoDB uses 'id' as the messageId
  phoneNumber: string
  timestamp: string
  message: string
  direction: 'inbound'
  read: boolean
  twilioSid: string
  userId: string
  fromPhoneNumber: string
  type: 'voicemail'
  duration: number
  s3Key: string
  recordingUrl: string
  recordingSid: string
  callSid: string
  audioUrl?: string // Pre-signed URL for playback
}

interface VoicemailsViewProps {
  apiUrl: string
  user: any
  selectedUserPhone: string | undefined
  contacts: { [phoneNumber: string]: { name: string; email?: string } }
  onCallClick: (customerNumber: string) => void
}

export function VoicemailsView({
  apiUrl,
  user,
  selectedUserPhone,
  contacts,
  onCallClick
}: VoicemailsViewProps) {
  const [voicemails, setVoicemails] = useState<VoicemailMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [deletingVoicemail, setDeletingVoicemail] = useState<string | null>(null)
  const [deleteConfirmVoicemail, setDeleteConfirmVoicemail] = useState<VoicemailMessage | null>(null)
  
  // Ref to prevent duplicate fetches in React Strict Mode
  const isFetchingRef = useRef(false)

  // Fetch voicemails
  const fetchVoicemails = useCallback(async () => {
    if (!apiUrl || !user) return
    
    // Prevent duplicate calls
    if (isFetchingRef.current) {
      console.log('🔄 Already fetching voicemails, skipping duplicate call')
      return
    }
    
    isFetchingRef.current = true
    setLoading(true)
    setError(null)
    try {
      const session = await fetchAuthSession()
      const jwtToken = session.tokens?.idToken?.toString()

      if (!jwtToken) return

      // Build URL with phone number filter if selected
      const url = selectedUserPhone 
        ? `${apiUrl}/app/voicemails?phoneNumber=${encodeURIComponent(selectedUserPhone)}`
        : `${apiUrl}/app/voicemails`;
      
      console.log('📞 Fetching voicemails from:', url)
      console.log('📞 Selected phone:', selectedUserPhone || 'All phones')

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
        }
      })

      if (response.ok) {
        const data = await response.json()
        console.log('✅ Fetched voicemails:', data.voicemails?.length || 0, 'total')
        setVoicemails(data.voicemails || [])
        
        // Fetch contacts for caller numbers we don't have yet
        // Contact info should already be available from conversation data
        // No need to fetch contacts separately for voicemails
      } else {
        console.error('❌ Voicemail fetch failed:', response.status, response.statusText)
        setError('Failed to load voicemails')
      }
    } catch (error) {
      console.error('📥 Error fetching voicemails:', error)
      setError('Error loading voicemails')
    } finally {
      setLoading(false)
      isFetchingRef.current = false
    }
  }, [apiUrl, user, selectedUserPhone])

  // Initial load and when selected phone changes
  useEffect(() => {
    fetchVoicemails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, user, selectedUserPhone])

  // Mark voicemail as read
  const markAsRead = useCallback(async (voicemailId: string, customerNumber: string) => {
    if (!apiUrl || !user) return

    try {
      const session = await fetchAuthSession()
      const jwtToken = session.tokens?.idToken?.toString()

      if (!jwtToken) return

      await fetch(`${apiUrl}/app/messages/mark-read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          customerNumber,
          fromPhoneNumber: selectedUserPhone
        })
      })

      // Optimistic update
      setVoicemails(prev =>
        prev.map(vm =>
          vm.id === voicemailId ? { ...vm, read: true } : vm
        )
      )
    } catch (error) {
      console.error('Error marking voicemail as read:', error)
    }
  }, [apiUrl, user, selectedUserPhone])

  // Mark voicemail as unread
  const markAsUnread = useCallback(async (voicemailId: string, customerNumber: string) => {
    if (!apiUrl || !user) return

    try {
      const session = await fetchAuthSession()
      const jwtToken = session.tokens?.idToken?.toString()

      if (!jwtToken) return

      await fetch(`${apiUrl}/app/messages/mark-unread`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          customerNumber,
          fromPhoneNumber: selectedUserPhone
        })
      })

      // Optimistic update
      setVoicemails(prev =>
        prev.map(vm =>
          vm.id === voicemailId ? { ...vm, read: false } : vm
        )
      )
    } catch (error) {
      console.error('Error marking voicemail as unread:', error)
    }
  }, [apiUrl, user, selectedUserPhone])

  // Delete voicemail
  const deleteVoicemail = useCallback(async (vm: VoicemailMessage) => {
    if (!apiUrl || !user) return

    setDeletingVoicemail(vm.id)
    try {
      const session = await fetchAuthSession()
      const jwtToken = session.tokens?.idToken?.toString()

      if (!jwtToken) return

      const response = await fetch(`${apiUrl}/app/voicemails/${encodeURIComponent(vm.id)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
        }
      })

      if (response.ok) {
        // Optimistic removal from UI
        setVoicemails(prev => prev.filter(v => v.id !== vm.id))
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(errorData.error || 'Failed to delete voicemail')
      }
    } catch (error) {
      console.error('Error deleting voicemail:', error)
      alert(`Failed to delete voicemail: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setDeletingVoicemail(null)
    }
  }, [apiUrl, user])

  // Filter voicemails by selected phone and search
  const filteredVoicemails = useMemo(() => {
    let filtered = voicemails

    // Filter by selected user phone
    if (selectedUserPhone) {
      filtered = filtered.filter(vm => vm.phoneNumber === selectedUserPhone)
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(vm => {
        const callerName = contacts[vm.fromPhoneNumber]?.name || ''
        const callerPhone = vm.fromPhoneNumber || ''
        return callerName.toLowerCase().includes(query) || 
               callerPhone.toLowerCase().includes(query)
      })
    }

    return filtered
  }, [voicemails, selectedUserPhone, searchQuery, contacts])

  // Group voicemails by date
  const groupedVoicemails = useMemo(() => {
    const groups: { [date: string]: VoicemailMessage[] } = {}

    filteredVoicemails.forEach(vm => {
      // Convert to EST/EDT timezone for proper date grouping
      const date = new Date(vm.timestamp).toLocaleDateString('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
      if (!groups[date]) {
        groups[date] = []
      }
      groups[date].push(vm)
    })

    return groups
  }, [filteredVoicemails])

  const formatPhoneNumber = (phone: string) => {
    return phone.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '$1-$2-$3-$4')
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    if (mins > 0) {
      return `${mins}m ${secs}s`
    }
    return `${secs}s`
  }

  const formatDateHeader = (dateString: string) => {
    const date = new Date(dateString)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    
    // Check if it's today
    if (date.toDateString() === today.toDateString()) {
      return 'Today'
    }
    
    // Check if it's yesterday
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday'
    }
    
    // For other dates, show a more readable format
    return date.toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getDisplayName = (phoneNumber: string) => {
    return contacts[phoneNumber]?.name || formatPhoneNumber(phoneNumber)
  }


  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b bg-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Voicemail className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Voicemails</h2>
            {filteredVoicemails.length > 0 && (
              <span className="text-sm text-muted-foreground">
                ({filteredVoicemails.filter(vm => !vm.read).length} unread)
              </span>
            )}
          </div>
          <button
            onClick={fetchVoicemails}
            className="text-sm text-primary hover:underline"
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder="Search by name or number..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      {/* Voicemail List */}
      <div className="flex-1 overflow-y-auto">
        {loading && voicemails.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <p>{error}</p>
          </div>
        ) : filteredVoicemails.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-8 text-center">
            <Voicemail className="h-12 w-12 mb-4 opacity-20" />
            <p className="text-lg font-medium">No voicemails</p>
            <p className="text-sm mt-1">
              {searchQuery ? 'No voicemails match your search' : 'Voicemails will appear here'}
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {Object.keys(groupedVoicemails)
              .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
              .map(date => (
                <div key={date}>
                  {/* Date Header */}
                  <div className="sticky top-0 bg-background px-4 py-2 text-xs font-medium text-muted-foreground flex items-center gap-2 z-10">
                    <Calendar className="h-3 w-3" />
                    {formatDateHeader(date)}
                  </div>

                  {/* Voicemails for this date */}
                  {groupedVoicemails[date].map(vm => (
                    <div
                      key={vm.id}
                      className={`p-4 transition-colors border-l-2 ${
                        !vm.read ? 'bg-accent/10 border-l-primary' : 'border-l-transparent'
                      } ${deletingVoicemail === vm.id ? 'opacity-50 pointer-events-none' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Read/Unread Indicator */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            if (vm.read) {
                              markAsUnread(vm.id, vm.fromPhoneNumber)
                            } else {
                              markAsRead(vm.id, vm.fromPhoneNumber)
                            }
                          }}
                          className="mt-1 text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
                          title={vm.read ? 'Mark as unread' : 'Mark as read'}
                        >
                          {vm.read ? (
                            <CheckCircle className="h-4 w-4" />
                          ) : (
                            <Circle className="h-4 w-4 fill-primary text-primary" />
                          )}
                        </button>

                        {/* Voicemail Content */}
                        <div className="flex-1 min-w-0">
                          {/* Caller Info & Time */}
                          <div className="flex items-start justify-between mb-2 gap-2">
                            <div className="flex-1 min-w-0">
                              <h3 className={`truncate ${!vm.read ? 'font-semibold' : 'font-medium'}`}>
                                {getDisplayName(vm.fromPhoneNumber)}
                              </h3>
                              {!contacts[vm.fromPhoneNumber]?.name && (
                                <p className="text-xs text-muted-foreground">
                                  {formatPhoneNumber(vm.fromPhoneNumber)}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
                              <Clock className="h-3 w-3" />
                              {new Date(vm.timestamp).toLocaleTimeString('en-US', { 
                                timeZone: 'America/New_York',
                                hour: '2-digit', 
                                minute: '2-digit',
                                hour12: true
                              })}
                            </div>
                          </div>

                          {/* Audio Player */}
                          {vm.audioUrl && (
                            <div className="mb-3">
                              <audio
                                controls
                                className="w-full"
                                style={{ height: '32px' }}
                                onPlay={() => {
                                  if (!vm.read) {
                                    markAsRead(vm.id, vm.fromPhoneNumber)
                                  }
                                }}
                              >
                                <source src={vm.audioUrl} type="audio/mpeg" />
                                Your browser does not support the audio element.
                              </audio>
                            </div>
                          )}

                          {/* Duration & Actions */}
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Voicemail className="h-3 w-3" />
                              <span>{formatDuration(vm.duration)}</span>
                            </div>

                            <div className="flex items-center gap-2">
                              {/* Call Back Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onCallClick(vm.fromPhoneNumber)
                                }}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors"
                              >
                                <Phone className="h-3 w-3" />
                                Call Back
                              </button>

                              {/* Delete Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setDeleteConfirmVoicemail(vm)
                                }}
                                disabled={deletingVoicemail === vm.id}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-input bg-background hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-colors disabled:opacity-50"
                                title="Delete voicemail"
                              >
                                <Trash2 className="h-3 w-3" />
                                {deletingVoicemail === vm.id ? 'Deleting...' : 'Delete'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmVoicemail && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="rounded-lg border bg-card text-card-foreground shadow-lg w-full max-w-md">
            <div className="flex flex-col space-y-1.5 p-6 border-b">
              <h3 className="text-lg font-semibold leading-none tracking-tight">Delete Voicemail</h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete this voicemail from{' '}
                <span className="font-medium text-foreground">{getDisplayName(deleteConfirmVoicemail.fromPhoneNumber)}</span>?
              </p>
              <p className="text-xs text-muted-foreground">
                This action cannot be undone. The voicemail recording will be permanently deleted.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 p-6 pt-0">
              <button
                onClick={() => setDeleteConfirmVoicemail(null)}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-4"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  deleteVoicemail(deleteConfirmVoicemail)
                  setDeleteConfirmVoicemail(null)
                }}
                disabled={deletingVoicemail === deleteConfirmVoicemail.id}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 bg-destructive text-destructive-foreground hover:bg-destructive/90 h-9 px-4 disabled:opacity-50"
              >
                {deletingVoicemail === deleteConfirmVoicemail.id ? (
                  <>
                    <LoadingSpinner size="sm" hideText />
                    <span className="ml-2">Deleting...</span>
                  </>
                ) : (
                  'Delete Voicemail'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}